import $ from 'jquery';

import ensureCsrf from 'chairisher/util/csrf';
import loadScript from 'chairisher/util/script';

import { getRecaptchaActionName, getRecaptchaSiteKey } from 'chairisher/context/auth';
import { getRecaptchaJSUrl } from 'chairisher/context/script';
import { handleAsyncError } from 'chairisher/util/logging';

let loadingPromise;

/**
 * Loads google's recaptcha library.
 *
 * @return {Promise<object>}
 */
function loadRecaptcha() {
    if (!loadingPromise) {
        loadingPromise = loadScript({ scriptName: 'grecaptcha', scriptSrc: getRecaptchaJSUrl() });
    }

    return loadingPromise;
}

/**
 * Executes recaptcha v3: gets a unique token from google and returns a deferred with the token and action name as
 * data. When formEl is provided, embeds into that form via name selectors. Otherwise falls back to the legacy
 * global #id_recaptcha_* inputs used by account auth forms.
 *
 * @param {string} actionName a string to identify what action the user took
 * @param {HTMLElement|jQuery=} formEl optional form to embed the token into
 * @returns {jQuery.Deferred}
 */
export default function executeRecaptcha(actionName, formEl = null) {
    const deferred = $.Deferred();
    const siteKey = getRecaptchaSiteKey();
    if (siteKey) {
        loadRecaptcha().then((recaptcha) => {
            recaptcha.ready(() => {
                recaptcha.execute(siteKey, { action: actionName }).then((token) => {
                    let recaptchaData = {};
                    if (token) {
                        if (formEl) {
                            const $form = $(formEl);
                            $form.find('[name=recaptcha_token]').val(token);
                            $form.find('[name=recaptcha_action]').val(actionName);
                        } else {
                            // embed token in form and send to backend to verify on form submission
                            $('#id_recaptcha_token').val(token);
                            $('#id_recaptcha_action').val(actionName);
                        }
                        recaptchaData = {
                            recaptcha_action: actionName,
                            recaptcha_token: token,
                        };
                    }
                    deferred.resolve(recaptchaData);
                }, handleAsyncError);
            });
        }, handleAsyncError);
    } else {
        deferred.resolve();
    }
    return deferred;
}

/**
 * Runs reCAPTCHA and writes token/action into the given form.
 *
 * @param {HTMLElement|jQuery} formEl
 * @returns {Promise<object|undefined>}
 */
export async function executeRecaptchaForForm(formEl) {
    const $form = $(formEl);
    return executeRecaptcha($form.find('[name=recaptcha_action]').val(), $form);
}

/**
 * Runs reCAPTCHA for a form, then CSRF-prepares and submits it so bound submit handlers run.
 *
 * @param {HTMLElement|jQuery} formEl
 * @returns {Promise<object|undefined>}
 */
export function submitFormWithRecaptcha(formEl) {
    const $form = $(formEl);
    const actionName = $form.find('[name=recaptcha_action]').val() || getRecaptchaActionName();
    return executeRecaptcha(actionName, $form).then((recaptchaData) => {
        ensureCsrf();
        // requestSubmit fires submit handlers (favorites AJAX, etc.); form.submit() bypasses them
        $form[0].requestSubmit();
        return recaptchaData;
    });
}
